import { createEntityAdapter } from '@reduxjs/toolkit';
import { apiSlice } from '@/slices/apiSlice';
import axios from 'axios';
import { requestCustomerPhoto, recordSavedCustomerPhoto, customerPhotoRequestError } from '@/utils/customerPhotoRequests';

const customersAdapter = createEntityAdapter({
	selectId: (entity) => {
		return entity?._id;
	}, // Use _id instead of id
});

const initialState = customersAdapter.getInitialState();

// Merge only confirmed photo changes; a failed upload leaves every view intact.
function applySavedCustomerPhoto(customer, { dispatch, getState }) {
	recordSavedCustomerPhoto(customer);
	for (const endpoint of ['getCustomers', 'getCustomerById', 'getCustomerPhoto']) {
		for (const args of extendedApiSlice.util.selectCachedArgsForQuery(getState(), endpoint)) {
			dispatch(extendedApiSlice.util.updateQueryData(endpoint, args, (draft) => {
				const cached = endpoint === 'getCustomerPhoto' ? draft : draft?.entities?.[customer._id];
				if (cached?._id === customer._id) cached.photoUrl = customer.photoUrl;
			}));
		}
	}
	dispatch({ type: 'CUSTOMER_PHOTO_SAVED', payload: customer });
}

async function saveCustomerPhoto({ _customer, _business, file }, api, remove = false) {
	try {
		const body = new FormData();
		if (file) body.append('photo', file);
		const { data } = await axios({
			url: `/api/customers/${encodeURIComponent(_customer)}/photo`,
			method: remove ? 'DELETE' : 'PUT',
			params: _business ? { _business } : undefined,
			data: remove ? undefined : body,
			withCredentials: true,
			timeout: 60000,
		});
		if (data.customer?._id !== _customer || !Object.hasOwn(data.customer, 'photoUrl')) {
			throw new Error('Invalid customer photo response');
		}
		applySavedCustomerPhoto(data.customer, api);
		return { data: data.customer };
	} catch (error) {
		return { error: customerPhotoRequestError(error) };
	}
}

const photoTags = (result, error, { _customer }) => error ? [] : [
	{ type: 'Customer', id: _customer },
	{ type: 'CustomerPhoto', id: _customer },
];

export const extendedApiSlice = apiSlice.enhanceEndpoints({ addTagTypes: ['CustomerPhoto'] }).injectEndpoints({
	endpoints: (builder) => ({
		getCustomerPhoto: builder.query({
			async queryFn({ _customer, _business }) {
				try {
					return { data: await requestCustomerPhoto(_customer, _business) };
				} catch (error) {
					return { error: customerPhotoRequestError(error) };
				}
			},
			providesTags: (result, error, { _customer }) => [{ type: 'CustomerPhoto', id: _customer }],
		}),
		updateCustomerPhoto: builder.mutation({
			queryFn: (args, api) => saveCustomerPhoto(args, api),
			invalidatesTags: photoTags,
		}),
		removeCustomerPhoto: builder.mutation({
			queryFn: (args, api) => saveCustomerPhoto(args, api, true),
			invalidatesTags: photoTags,
		}),
		getCustomers: builder.query({
			query: () => '/customers',
			transformResponse: (responseData) => {
				return customersAdapter.setAll(initialState, responseData);
			},
			providesTags: (result) => [
				{ type: 'Customer', id: 'LIST' }, // This will invalidate all queries with the tag 'Customer'
				...(result?.ids?.map((_id) => ({
					type: 'Customer',
					id: _id,
				})) || []), // This provides a tag for each customer based on the ID
			],
		}),

		getCustomerById: builder.query({
			query: (_id) => `/customers/get/${_id}`,
			transformResponse: (responseData) => {
				// Merge the single customer into the existing list of customers
				return customersAdapter.upsertMany(initialState, [
					responseData,
				]);
			},
			providesTags: (result, error, id) => [{ type: 'Customer', id }],
		}),

		addProperty: builder.mutation({
			query: ({ _customer, propertyData }) => ({
				url: `/customers/create-property`,
				method: 'POST',
				body: { propertyData, _customer },
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: 'Customer', id: arg._customer },
			],
		}),

		deleteProperty: builder.mutation({
			query: ({ _customer, _property }) => ({
				url: `/customers/delete-property`,
				method: 'DELETE',
				body: { _customer, _property },
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: 'Customer', id: arg._customer },
			],
			// add optimistic update
			async onQueryStarted(
				{ _customer, _property },
				{ dispatch, queryFulfilled }
			) {
				// Optimistically update the cache
				const patchResult = dispatch(
					extendedApiSlice.util.updateQueryData(
						'getCustomers', // The endpoint name
						'getCustomers', // The cache key
						(draft) => {
							const customer = draft.entities[_customer]; // Get the specific customer by ID

							if (customer) {
								customer._properties =
									customer._properties.filter(
										(p) => p._id !== _property
									);
							}
						}
					)
				);

				// Optimistically update the cache for the specific customer (getCustomerById)
				const patchCustomerResult = dispatch(
					extendedApiSlice.util.updateQueryData(
						'getCustomerById', // The endpoint name for fetching a single customer
						_customer, // Use the customer ID as the cache key
						(draft) => {
							if (draft) {
								const customer = draft.entities[_customer];

								// Remove the property from the _properties list for the specific customer
								customer._properties =
									customer._properties.filter(
										(p) => p._id !== _property
									);
							}
						}
					)
				);

				try {
					// Wait for the query to finish (either successfully or with an error)
					await queryFulfilled;
				} catch {
					// If the mutation fails, undo the optimistic update
					patchResult.undo();
					patchCustomerResult.undo();
				}
			},
		}),

		createCustomer: builder.mutation({
			query: (body) => ({
				url: `/customers/create`,
				method: 'POST',
				body: { ...body },
			}),

			invalidatesTags: () => [{ type: 'Customer', id: 'LIST' }],

			transformResponse: (responseData) => {
				// Merge the single customer into the existing list of customers
				return customersAdapter.upsertMany(initialState, [
					responseData,
				]);
			},
		}),

		updateCustomer: builder.mutation({
			query: (data) => ({
				url: `/customers/update`,
				method: 'PUT',
				body: {
					...data,
					_id: data._customer,
				},
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: 'Customer', id: arg._customer },
			],
			transformResponse: (responseData) => {
				const { customer } = responseData;
				if (!customer) {
					return initialState;
				}
				// Merge the single customer into the existing list of customers
				return customersAdapter.upsertMany(initialState, [customer]);
			},
			// add optimistic update
			async onQueryStarted(data, { dispatch, queryFulfilled }) {
				const _customer = data._customer;

				const newCustomer = {
					...data,
					_id: data._customer,
				};
				// `updateQueryData` requires the endpoint name and cache key arguments,
				// so it knows which piece of cache state to update
				const patchResult = dispatch(
					// updateQueryData takes three arguments: the name of the endpoint to update, the same cache key value used to identify the specific cached data, and a callback that updates the cached data.
					extendedApiSlice.util.updateQueryData(
						'getCustomers',
						'getCustomers',
						(draft) => {
							// The `draft` is Immer-wrapped and can be "mutated" like in createSlice
							const customer = draft.entities[_customer];
							if (customer) {
								Object.assign(customer, newCustomer);
							}
						}
					)
				);

				// Optimistically update the cache for the specific customer (getCustomerById)
				const patchCustomerResult = dispatch(
					extendedApiSlice.util.updateQueryData(
						'getCustomerById', // The endpoint name for fetching a single customer
						_customer, // Use the customer ID as the cache key
						(draft) => {
							if (draft) {
								const customer = draft.entities[_customer];
								if (customer) {
									Object.assign(customer, newCustomer);
								}
							}
						}
					)
				);
				try {
					await queryFulfilled;
				} catch {
					patchResult.undo();
					patchCustomerResult.undo();
				}
			},
		}),

		deleteCustomer: builder.mutation({
			query: ({ _id }) => ({
				url: `/customers/delete`,
				method: 'POST',
				body: { _id },
			}),
			invalidatesTags: (result, error, arg) => [
				{ type: 'Customer', id: arg._id },
				{ type: 'Customer', id: 'LIST' },
			],
		}),

		// Deletes a batch through the single-contact route, a few at a time.
		// Never rejects: returns which ids were deleted and which failed. It
		// invalidates nothing, so callers sending several batches refresh the
		// list once at the end instead of after every batch.
		deleteCustomers: builder.mutation({
			async queryFn({ _ids }, _api, _extraOptions, baseQuery) {
				const deleted = [];
				const failed = [];
				let next = 0;
				const worker = async () => {
					while (next < _ids.length) {
						const _id = _ids[next++];
						const res = await baseQuery({
							url: '/customers/delete',
							method: 'POST',
							body: { _id },
						});
						if (res.error || res.data?.error) failed.push(_id);
						else deleted.push(_id);
					}
				};
				await Promise.all(
					Array.from({ length: Math.min(5, _ids.length) }, worker)
				);
				return { data: { deleted, failed } };
			},
		}),
	}),
});

// Export the auto-generated hooks
export const {
	useGetCustomerPhotoQuery,
	useUpdateCustomerPhotoMutation,
	useRemoveCustomerPhotoMutation,
	useGetCustomersQuery,
	useGetCustomerByIdQuery,
	useLazyGetCustomerByIdQuery,
	useUpdateCustomerMutation,
	useDeleteCustomerMutation,
	useDeleteCustomersMutation,
	useAddPropertyMutation,
	useDeletePropertyMutation,
	useCreateCustomerMutation,
} = extendedApiSlice;

// Export the utility functions for manual invalidation/refetching
export const { util } = extendedApiSlice;
